import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import clone from 'just-clone';
import { combineLatest, Observable, ReplaySubject, Subscriber } from 'rxjs';
import { filter, map, takeWhile, tap, withLatestFrom } from 'rxjs/operators';
import * as fromSettings from '../../company-setting/store';
import { IGroup } from '../../groups/IGroups';
import * as fromCommon from '../../shared';
import * as fromShared from '../../shared';
import { SelectItem } from '../../shared/efaact-primeng.module';
import * as fromLoading from '../../shared/loading';
import { LoggingService } from '../../shared/services/logging/logging.service';
import * as fromStore from '../store';
import { Reminder } from '../store/reminder.model';
@Component({
    selector: 'ef-reminder-container',
    templateUrl: './reminder-container.component.html',
    styleUrls: ['./reminder-container.component.scss'],
    standalone: false
})
export class ReminderContainerComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    showAddReminder = false;
    public reminders$: Observable<Reminder[]>;
    public loading$: Observable<boolean>;
    public reminderStatus$: Observable<boolean>;
    public reminderScheduleTypes$: Observable<SelectItem[]>;
    public reminderTypes$: Observable<SelectItem[]>;
    public recipients$: Observable<IGroup[]>;
    public selectedError$: Observable<fromShared.Errors.IShowErrors>;
    public selectedErrorWithStatus$: Observable<fromCommon.IStatusMessage>;
    public reminderReviewPeriods$: Observable<SelectItem[]>;
    public reminderSuccess$: Observable<Reminder[]>;
    public reminderGroups$: Observable<{ id: number; groups: number[] }>;
    private reminderGroupsSubscriber: Subscriber<{
        id: number;
        groups: number[];
    }>;
    active = true;
    showPopup = false;
    messages: string[];
    process$: Observable<fromShared.Process>;
    ShowErrorSubject = new ReplaySubject<fromShared.Errors.IShowErrors>(1);

    constructor(
        private store: Store<fromStore.State>,
        private log: LoggingService
    ) {
        this.setupObservables();
    }

    ngOnInit() {
        this.store.dispatch(fromSettings.getCompanySettings());
        this.store.dispatch(fromStore.getReminderReviewPeriods());
        this.store.dispatch(fromStore.getReminderTypes());
        this.store.dispatch(fromStore.getReminderScheduleTypes());
        this.store.dispatch(fromStore.getReminderRecipients());
        this.store.dispatch(fromStore.getReminders());
        this.reminderGroups$ = new Observable(s => {
            this.reminderGroupsSubscriber = s;

            this.setReminderGroups(s);

            return function unsubscribe() {};
        });
    }
    ngAfterViewInit() {
        this.setupSubscriptions();
    }
    setReminderGroups(subscriber) {
        subscriber.next([
            { id: 1, groups: [12027, 12028] },
            { id: 2, groups: [12027, 12028] },
            { id: 5, groups: [12027, 12028] },
            { id: 3, groups: [12024, 12025] },
            { id: 4, groups: [12024, 12025] }
        ]);
    }
    ngOnDestroy() {
        this.active = false;
    }
    setupObservables() {
        this.process$ = this.store.pipe(select(fromShared.selectCurrent));

        this.reminders$ = this.store.pipe(
            select(fromStore.selectAll),
            map(reminders =>
                clone(reminders).sort((a: Reminder, b: Reminder) =>
                    a.Remindertypename.localeCompare(b.Remindertypename) === 0
                        ? a.Reminderinstancename.localeCompare(
                              b.Reminderinstancename
                          )
                        : a.Remindertypename.localeCompare(b.Remindertypename)
                )
            )
        );
        this.selectedError$ = this.store.pipe(
            select(fromShared.Errors.getShowErrors)
        );
        const errors$ = this.store.pipe(select(fromShared.Errors.getErrors));
        this.selectedErrorWithStatus$ = combineLatest([
            this.selectedError$,
            errors$,
            this.reminders$.pipe(filter(x => !!x))
        ]).pipe(
            tap(([selected, errors]) =>
                this.log.verbose({
                    type: 'PayrollItemFeatureComponent:errors$',
                    errors,
                    selected
                })
            ),
            map(
                ([selected, errors]) =>
                    errors.filter(
                        e =>
                            e.MsgEntityID === selected.MsgEntityID &&
                            e.MsgProcessID === selected.MsgProcessID
                    )[0]
            ),
            tap(selected =>
                this.log.verbose({
                    type: 'PayrollItemFeatureComponent:errors$:filtered',
                    selected
                })
            )
        );
        this.reminderSuccess$ = this.store.pipe(
            select(fromStore.selectAll),
            map(reminders =>
                reminders.filter(
                    x =>
                        x.Status.StatusCode ===
                        fromCommon.ResponseStatus.Success
                )
            ),
            filter(x => !!x && x.length > 0)
        );

        this.reminderScheduleTypes$ = this.store.pipe(
            select(fromStore.selectReminderScheduleTypes),
            map(items =>
                items.map(i => ({
                    label: i.ReminderScheduleDesc,
                    value: i.ReminderScheduleID
                }))
            )
        );
        this.reminderTypes$ = this.store.pipe(
            select(fromStore.selectReminderTypes),
            map(items =>
                items.map(i => ({
                    label: i.Remindertypename,
                    value: i.Remindertypeid
                }))
            )
        );
        this.recipients$ = this.store.pipe(
            select(fromStore.selectReminderRecipients),
            map(items =>
                items.map(item => ({
                    ...item,
                    GroupName: `${item.GroupName}${
                        item.IsRole ? '' : ' (custom)'
                    }`
                }))
            )
            // map(items =>
            //     items.map(i => ({
            //         label: i.GroupName,
            //         value: i.GroupID
            //     }))
            // )
        );

        this.loading$ = this.store.pipe(select(fromLoading.getLoading));
        this.reminderStatus$ = this.store.pipe(
            select(fromSettings.getReminderStatus),
            map(x =>
                x && (x.SettingValue === '1' || x.SettingValue === 1)
                    ? true
                    : false
            )
        );
        this.reminderReviewPeriods$ = this.store.pipe(
            select(fromStore.selectReminderReviewPeriods),
            map(items =>
                items.map(i => ({
                    label: i.Reminderreviewperiodname,
                    value: i.Reminderreviewperiod
                }))
            )
        );
        combineLatest([this.process$, this.ShowErrorSubject])
            .pipe(takeWhile(() => this.active))
            .subscribe(([process, event]) => {
                this.store.dispatch(
                    new fromShared.Errors.SetShowErrors({
                        open: event.open,
                        MsgEntityID: event.MsgEntityID,
                        MsgProcessID: process.ProcessID
                    })
                );
            });
    }
    setupSubscriptions() {
        // combineLatest([this.reminderStatus$, this.reminderSuccess$])
        this.reminderSuccess$
            .pipe(
                takeWhile(() => this.active),
                withLatestFrom(this.reminderStatus$),
                tap(([status, reminders]) =>
                    this.log.verbose({
                        type: 'ReminderContainerComponent:updatedreminder',
                        status,
                        reminders
                    })
                )
            )
            .subscribe(([reminders, status]) => {
                if (!status) {
                    this.messages = [
                        'Reminders successfully saved. Remember to turn "ON" reminders using the toggle at the top of the page.'
                    ];
                    this.showPopup = true;
                }
            });
    }
    CancelNewReminder() {
        this.showAddReminder = false;
    }
    onSaveReminder($event: Reminder) {
        this.log.verbose({
            type: 'ReminderContainerComponent:onSaveReminder',
            $event
        });
        this.store.dispatch(fromStore.putReminders({ reminders: [$event] }));
    }
    onApplyAction($event: Reminder) {
        this.log.verbose({
            type: 'ReminderContainerComponent:onApplyAction',
            $event
        });
        this.store.dispatch(
            fromStore.removeReminder({ id: $event.Reminderinstanceid })
        );
    }
    onRunNow($event: Reminder) {
        this.store.dispatch(
            fromStore.runReminder({
                Reminderinstanceid: $event.Reminderinstanceid
            })
        );
    }
    onAddReminder(reminder: Reminder) {
        this.store.dispatch(fromStore.putReminders({ reminders: [reminder] }));
        this.showAddReminder = false;
    }
    onToggleReminders($event) {
        this.log.verbose({
            type: 'ReminderContainerComponent:onToggleReminders',
            $event
        });
        this.store.dispatch(
            fromSettings.putCompanySettings({
                settings: [
                    {
                        CtrlType: 'checkbox',
                        SettingType: 'RmdrStatus',
                        SettingValue: $event ? 1 : 0
                    }
                ]
            })
        );
    }
    onShowError(event) {
        this.ShowErrorSubject.next(event);
    }
}
